import {bbox, toMercator} from '@turf/turf';
import classnames from 'classnames';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import COLORS from 'app/styles/colors.json';
import * as C from 'app/utils/constants';
import {BBox2d, bboxPolygon} from 'app/utils/geoJsonUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';

import cs from './styles.module.scss';

const DEFAULT_STROKE_WIDTH = 3;
const MAX_ZOOM = 2;
// How much of the stage the feature may fill. Small stages keep the 360px cap so the feature
// still reads; larger stages grow it in proportion.
const FEATURE_FILL_RATIO = 360 / 420;
const MIN_MAX_FEATURE_SIZE = 360;

const PREVIEW_IMAGE_CLIP_PATH_ID = 'preview-image-clipping';

const ImageryPreviewImage: React.FunctionComponent<
  React.PropsWithChildren<{
    feature: I.ImmutableOf<ApiFeature>;
    url: string | undefined;
    bounds: geojson.Polygon | geojson.MultiPolygon | BBox2d;
    maxWidth: number;
    maxHeight: number;
    sourceId: string;
    sceneGeometry: BBox2d | geojson.Polygon | geojson.MultiPolygon;
    subsetFeature?: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null;
    blockedSources?: string[];
    isFeatureHidden?: boolean;
    orderedGeometry?: geojson.Polygon | geojson.MultiPolygon;
    thumbnailOverrideUrl?: string | undefined;
  }>
> = ({
  feature,
  url,
  bounds,
  maxWidth,
  maxHeight,
  sourceId,
  sceneGeometry,
  blockedSources = C.THUMBNAIL_BLOCKED_SOURCES,
  isFeatureHidden,
  subsetFeature,
  orderedGeometry,
  thumbnailOverrideUrl,
}) => {
  const isBlockedSource = blockedSources.includes(sourceId);
  const imageSrc = isBlockedSource ? '' : thumbnailOverrideUrl ? thumbnailOverrideUrl : url;

  // If we are showing the preview without a url, set the dimensions to the max width
  // and height so we can still show the outlines of the feature and the order area
  const [imgDimensions, setImgDimensions] = useStateWithDeps<{
    width: number;
    height: number;
  } | null>(imageSrc || isBlockedSource ? null : {width: maxWidth, height: maxHeight}, [
    imageSrc,
    isBlockedSource,
    maxHeight,
    maxWidth,
  ]);

  const geometry: geojson.Polygon | geojson.MultiPolygon = React.useMemo(
    () => feature.get('geometry').toJS(),
    [feature]
  );
  const mercatorFeatureGeometry = toMercator(geometry);
  const mercatorBbox = bbox(mercatorFeatureGeometry);

  const [w, s, e, n] = mercatorBbox;

  // Used to properly position image in the canvas
  const mercatorImageBoundsFeature = toMercator(
    Array.isArray(bounds) ? bboxPolygon(bounds).geometry : bounds
  );
  const mercatorImageBbox = bbox(mercatorImageBoundsFeature);
  const mercatorImageWidth = mercatorImageBbox[2] - mercatorImageBbox[0];
  const mercatorImageHeight = mercatorImageBbox[3] - mercatorImageBbox[1];

  const mercatorSubsetGeometry = subsetFeature && toMercator(subsetFeature?.geometry);
  const mercatorOrderedGeometry = orderedGeometry && toMercator(orderedGeometry);
  const mercatorSceneGeometry =
    sceneGeometry &&
    toMercator(Array.isArray(sceneGeometry) ? bboxPolygon(sceneGeometry).geometry : sceneGeometry);

  // We take the bounding box of the feature in mercator coordinates, scale it to fit
  // most of the stage, then translate it to canvas xy. We then translate the thumbnail
  // to canvas xy to position the image correctly in relation to the the feature.
  //
  // When scaling our image and feature, we check how big and zoomed-in the resulting
  // image would be. If it would be too zoomed in to be useful (eg, for gigantic AIRBUS
  // EXTENDED ARCHIVE images), we specially scale the feature down to be smaller so we
  // can show more of the image.
  const mercatorToCanvasXY = React.useMemo(() => {
    // This will be null until the image loads because we don't have its pixel
    // dimensions until then
    if (!imgDimensions) {
      return null;
    }
    // Calculate the center of the feature in mercator coordinates
    const featureCenterX = (w + e) / 2;
    const featureCenterY = (s + n) / 2;

    // Calculate the center of the canvas
    const canvasCenterX = maxWidth / 2;
    const canvasCenterY = maxHeight / 2;

    // Calculate the width and height of the feature in mercator coordinates
    const featureWidth = e - w;
    const featureHeight = n - s;

    // Calculate the scale needed to fit the feature within its share of the stage
    const maxFeatureSize = Math.max(
      MIN_MAX_FEATURE_SIZE,
      Math.min(maxWidth, maxHeight) * FEATURE_FILL_RATIO
    );
    const scaleX = maxFeatureSize / featureWidth;
    const scaleY = maxFeatureSize / featureHeight;
    let scale = Math.min(scaleX, scaleY);

    // See how big our resized thumbnail will be.
    const potentialThumbnailPixelWidth = mercatorImageWidth * scale;
    const potentialThumbnailPixelHeight = mercatorImageHeight * scale;

    // If our thumbnail would be too big to be useful with our feature displayed
    // at a normal size, reset our scale so that the thumbnail (and feature) are
    // smaller. This is useful for gigantic Extended Archive images, where we want
    // to make sure users can see if their feature area is actually in a useful area
    // and not in the middle of a cloud or something.
    if (
      potentialThumbnailPixelWidth > imgDimensions.width * MAX_ZOOM ||
      potentialThumbnailPixelHeight > imgDimensions.height * MAX_ZOOM
    ) {
      const newXScale = (imgDimensions.width * MAX_ZOOM) / mercatorImageWidth;
      const newYScale = (imgDimensions.height * MAX_ZOOM) / mercatorImageHeight;
      scale = Math.min(newXScale, newYScale);
    }

    // Determine the translation needed to center the feature
    const translateX = canvasCenterX - featureCenterX * scale;
    const translateY = canvasCenterY - featureCenterY * scale;

    // We subtract imageHeight because DOM/SVG Y dimension is flipped relative to geographic Y dimension.
    return ([x, y]: [number, number]) => [
      x * scale + translateX,
      maxHeight - (y * scale + translateY),
    ];
  }, [imgDimensions, maxWidth, maxHeight, e, w, n, s, mercatorImageWidth, mercatorImageHeight]);

  // top and bottom may be wrong here but it works
  const leftTopImage =
    mercatorToCanvasXY && mercatorToCanvasXY([mercatorImageBbox[0], mercatorImageBbox[1]]);
  const rightBottomImage =
    mercatorToCanvasXY && mercatorToCanvasXY([mercatorImageBbox[2], mercatorImageBbox[3]]);

  const previewImageLeft = leftTopImage ? leftTopImage[0] : undefined;
  const previewImageTop = rightBottomImage ? rightBottomImage[1] : undefined;

  return (
    <div className={cs.previewImageWrapper} style={{width: maxWidth, height: maxHeight}}>
      {!isFeatureHidden && mercatorToCanvasXY && (
        <svg className={cs.previewFeature}>
          {renderGeojsonSvg(mercatorFeatureGeometry, mercatorToCanvasXY, {
            style: {
              stroke: 'transparent',
              strokeWidth: DEFAULT_STROKE_WIDTH,
              // Don't show red fill if we aren't displaying a thumbnail
              fill: imageSrc ? 'rgba(200, 0, 0, 0.6)' : 'transparent',
            },
          })}
        </svg>
      )}

      <img
        className={classnames(cs.previewImage, {
          [cs.grayscale]: !!thumbnailOverrideUrl,
        })}
        style={{
          display: mercatorToCanvasXY ? undefined : 'none',
          position: 'absolute',
          width:
            leftTopImage && rightBottomImage ? rightBottomImage[0] - leftTopImage[0] : undefined,
          height:
            leftTopImage && rightBottomImage ? leftTopImage[1] - rightBottomImage[1] : undefined,
          left: previewImageLeft,
          top: previewImageTop,
          clipPath: `url(#${PREVIEW_IMAGE_CLIP_PATH_ID})`,
        }}
        src={imageSrc}
        onLoad={(ev) =>
          setImgDimensions({width: ev.currentTarget.width, height: ev.currentTarget.height})
        }
      />

      {mercatorToCanvasXY && mercatorSceneGeometry && (
        <svg height="0" width="0">
          <defs>
            <clipPath
              id={PREVIEW_IMAGE_CLIP_PATH_ID}
              style={makePreviewImageClipPathStyle({previewImageLeft, previewImageTop})}
            >
              {renderGeojsonSvg(mercatorSceneGeometry, mercatorToCanvasXY)}
            </clipPath>
          </defs>
        </svg>
      )}

      {!isFeatureHidden && mercatorToCanvasXY && (
        <>
          {mercatorOrderedGeometry && (
            <svg className={cs.previewFeature}>
              {/* Already ordered Imagery */}
              {renderGeojsonSvg(mercatorOrderedGeometry, mercatorToCanvasXY, {
                style: {
                  stroke: COLORS.brightBlue,
                  strokeWidth: 1,
                  fill: 'rgba(19, 124, 189, 0.6)',
                },
              })}
            </svg>
          )}
          <svg className={cs.previewFeature}>
            {/* Feature outline */}
            {renderGeojsonSvg(mercatorFeatureGeometry, mercatorToCanvasXY, {
              style: {
                stroke: COLORS.brightBlue,
                strokeWidth: DEFAULT_STROKE_WIDTH,
                fill: 'transparent',
              },
            })}
          </svg>
          {mercatorSubsetGeometry && (
            <svg className={cs.previewFeature}>
              {/* AOI outline */}
              {renderGeojsonSvg(mercatorSubsetGeometry, mercatorToCanvasXY, {
                style: {
                  stroke: 'yellow',
                  strokeWidth: DEFAULT_STROKE_WIDTH,
                  fill: 'transparent',
                },
              })}
            </svg>
          )}
        </>
      )}
    </div>
  );
};

function coordToStr(c: [number, number], transform: ([x, y]: [number, number]) => number[]) {
  const [x, y] = transform(c);
  return `${x.toFixed(3)} ${y.toFixed(3)}`;
}

function coordsToPathCommands(
  coordinates: geojson.Polygon['coordinates'],
  transform: ([x, y]: [number, number]) => number[]
): string | undefined {
  return coordinates
    .map((ring) =>
      ring
        .map((c, i) => `${i === 0 ? 'M' : 'L'}${coordToStr(c as [number, number], transform)}`)
        .join(' ')
    )
    .join(' ');
}

export function renderGeojsonSvg(
  geometry: geojson.Polygon | geojson.MultiPolygon,
  transform: ([x, y]: [number, number]) => number[],
  props: React.SVGProps<SVGPathElement> = {}
): React.ReactNode {
  if (geometry.type === 'Polygon') {
    return <path {...props} d={coordsToPathCommands(geometry.coordinates, transform)} />;
  }

  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((poly, i) => (
      <path key={i} {...props} d={coordsToPathCommands(poly, transform)} />
    ));
  }

  return null;
}

export default ImageryPreviewImage;

function makePreviewImageClipPathStyle({
  previewImageLeft,
  previewImageTop,
}: {
  previewImageLeft?: number;
  previewImageTop?: number;
}): React.CSSProperties {
  const style: React.CSSProperties = {};

  if (previewImageLeft === undefined || previewImageTop === undefined) {
    /*
    If we don't have preview image coordinates, we don't know where to clip.
    */
    style.display = 'none';
  } else {
    /*
    We clip the preview image to the scene geometry in order to accurately
    communicate the area to be ordered. The scene geometry SVG path coordinates
    are relative to the top-left corner of the canvas, not the top-left corner
    of the preview image, which is the underlying assumption when applying an
    SVG path as a clip path. Thus, we will translate the path in the equal and
    opposite direction as the preview image so they align.
    */
    style.transform = `translate(${-1 * previewImageLeft}px, ${-1 * previewImageTop}px)`;
  }

  return style;
}
